// Share codes for a single crosshair config: "wbx1.<base64url(json)>".
// Import is always run through `normalizeConfig`, so a hand-edited or hostile
// code can only produce a valid, clamped config.

import { normalizeConfig } from "./config";
import type { CrosshairConfig } from "./types";

const PREFIX = "wbx1.";

function toBase64Url(bytes: Uint8Array): string {
  let bin = "";
  bytes.forEach((b) => (bin += String.fromCharCode(b)));
  return btoa(bin).replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

function fromBase64Url(s: string): Uint8Array | null {
  try {
    const b64 = s.replace(/-/g, "+").replace(/_/g, "/");
    const bin = atob(b64);
    return Uint8Array.from(bin, (c) => c.charCodeAt(0));
  } catch {
    return null;
  }
}

export function encodeCrosshair(cfg: CrosshairConfig): string {
  const json = JSON.stringify({
    s: cfg.scale,
    c: cfg.crisp,
    d: cfg.hideWhenDead,
    l: cfg.layers.map((l) => ({
      s: l.shape,
      v: l.visible,
      c: l.color,
      o: l.opacity,
      b: l.blend,
      x: l.offset.x,
      y: l.offset.y,
      r: l.rotation,
      z: l.scale,
      u: [l.outline.color, l.outline.thickness, l.outline.mode],
      p: l.params,
      f: l.fx.map((f) => [
        f.trigger, f.attack, f.hold, f.release, f.easing, f.retrigger,
        f.scale, f.rotate, f.gap, f.thickness, f.offsetX, f.offsetY, f.opacity,
        f.color, f.outlineColor,
      ]),
    })),
  });
  return PREFIX + toBase64Url(new TextEncoder().encode(json));
}

function expand(packed: unknown): unknown {
  if (!packed || typeof packed !== "object") return packed;
  const o = packed as Record<string, unknown>;
  const layers = Array.isArray(o.l) ? o.l : [];
  return {
    version: 1,
    scale: o.s,
    crisp: o.c,
    hideWhenDead: o.d,
    layers: layers.map((raw) => {
      const l = (raw && typeof raw === "object" ? raw : {}) as Record<string, unknown>;
      const u = Array.isArray(l.u) ? l.u : [];
      const f = Array.isArray(l.f) ? l.f : [];
      return {
        shape: l.s,
        visible: l.v,
        color: l.c,
        opacity: l.o,
        blend: l.b,
        offset: { x: l.x, y: l.y },
        rotation: l.r,
        scale: l.z,
        outline: { color: u[0], thickness: u[1], mode: u[2] },
        params: l.p,
        fx: f.map((rawFx, i) => {
          const a = Array.isArray(rawFx) ? rawFx : [];
          return {
            id: `f${i}`,
            trigger: a[0],
            attack: a[1],
            hold: a[2],
            release: a[3],
            easing: a[4],
            retrigger: a[5],
            scale: a[6],
            rotate: a[7],
            gap: a[8],
            thickness: a[9],
            offsetX: a[10],
            offsetY: a[11],
            opacity: a[12],
            color: a[13],
            outlineColor: a[14],
          };
        }),
      };
    }),
  };
}

export function decodeCrosshair(code: string): CrosshairConfig | null {
  const text = code.trim();
  if (!text.startsWith(PREFIX)) return null;
  const bytes = fromBase64Url(text.slice(PREFIX.length));
  if (!bytes) return null;
  try {
    const json = JSON.parse(new TextDecoder().decode(bytes)) as unknown;
    return normalizeConfig(expand(json));
  } catch {
    return null;
  }
}
